package it.maicol07.gamerlogue.ui.views.game.components

import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import at.released.igdbclient.model.Game
import at.released.igdbclient.model.GameTimeToBeat
import it.maicol07.gamerlogue.data.LibraryEntry
import it.maicol07.gamerlogue.ui.views.game.ReviewStats
import it.maicol07.gamerlogue.ui.views.list.GameListPreset

const val Ratio169 = 16f / 9f

private val ToolbarClearance = 96.dp

/**
 * Renders the scrollable content of the Game detail screen for a loaded [game].
 *
 * The sections themselves live next to this file, grouped by concern: `GameRatingSections.kt`,
 * `GameTaxonomySections.kt` and `GameMediaSections.kt`. Each one bails out on its own when the game
 * carries no data for it.
 */
internal fun LazyListScope.gameDetailContent(
    game: Game,
    editions: List<Game>,
    timeToBeat: GameTimeToBeat?,
    reviews: List<LibraryEntry>,
    reviewStats: ReviewStats?,
    ownEntry: LibraryEntry?,
    onReviewClick: (LibraryEntry) -> Unit,
    onAllReviewsClick: () -> Unit,
    onTitleVisibilityChange: (Boolean) -> Unit,
    onGameClick: (Game) -> Unit,
    onPresetClick: (GameListPreset) -> Unit
) {
    // Ordered by the questions a visitor asks: what it is and whether it is good (and what players
    // wrote about it), what it looks like,
    // how it plays, what else belongs to it, its other releases; then reference data, and similar
    // games last as the way out.
    item { GameHeader(game, onTitleVisibilityChange, onPresetClick) }
    item { GameRatings(game, reviewStats) }
    item { GameReviewsSection(reviews, reviewStats, ownEntry, onReviewClick, onAllReviewsClick) }
    item { GameDescription(game) }
    item { GameMedia(game) }
    item { GameGenresAndThemes(game, onPresetClick) }
    item { GameTimeToBeatSection(timeToBeat) }
    item { GameMultiplayerDetails(game) }
    item { GameFamily(game, editions, onGameClick) }
    item { GameOtherVersions(game, onGameClick) }
    item { GameDetailsList(game, onGameClick = onGameClick, onPresetClick = onPresetClick) }
    item { GameAgeRatings(game) }
    item { GameKeywords(game, onPresetClick) }
    item { GameWebsites(game) }
    item { GameSimilarGames(game, onGameClick) }
    // Room for the floating toolbar, so the last section can scroll clear of it.
    item { Spacer(Modifier.navigationBarsPadding().height(ToolbarClearance)) }
}
